<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query';
import type { PropType } from 'vue';

import { AuctionState, type UserMeta, type AuctionItemEntity } from '~/apiClient';
import { AUCTION_DETAIL_QUERY_KEY_PREFIX } from '~/constants/queryKeyPrefix';
import { isAuctionItemEntityWithCurrentPrince } from '~/types/auction.type';
import { NotificationDuration } from '~/types/notifications.type';
import { isAuctionItemEntityWithNegotiation } from '~/types/sale.type';

// PROPS
const { auction } = defineProps({
    auction: { type: Object as PropType<AuctionItemEntity>, required: true },
});

// COMPOSABLES
const queryClient = useQueryClient();
const { tt, tn } = useTypedI18n();
const { notifyError } = useNotification();
const { goToLoginPage } = useLoginRouteUtils();
const userGatedAction = useUserGatedActionState();
const { isLoggedIn } = useAuthUtils();
const { trackAdpAlertView, trackAdpPlaceBid, updateTrackingCompanyMetrics } = useTracking();
const { hasRestrictedNotice, hasInspectionNotice, isBailNeeded, activeAutoBidAmount, isSeller } = useAuctionStatus(toRef(() => auction));
const inspectionExpiredModalState = useModalState<boolean>();
const bailModalState = useModalState<boolean>();
const bidModalState = useModalState<boolean>();

// DATA
const hasPlaceBid = computed(() => auction.state === AuctionState.Running);
const hasGoToNegotiation = computed(() => isLoggedIn.value && isAuctionItemEntityWithNegotiation(auction));
const showSection = computed(() => hasRestrictedNotice.value || hasInspectionNotice.value || hasPlaceBid.value || hasGoToNegotiation.value);
const userMeta = computed<UserMeta | undefined>(() => auction.userMeta);
const shouldShowInspectionExpiredModal = computed(
    () =>
        !!userMeta.value?.inspectionExpiredExportRestrictions &&
        (isBailNeeded.value || (userMeta.value.isWhitelisted && userMeta.value.userBids === 0))
);
const bidFlowInProgress = ref(false);
const bidButtonLabel = computed(() => (userMeta.value?.autoBid?.active ? tt(`sdp.Auction.bidButtonActiveAutoBid`) : tt(`sdp.Auction.bidButton`)));

// HANDLERS
const bidFlow = async (): Promise<void> => {
    bidFlowInProgress.value = true;
    trackAdpPlaceBid();

    await (async (): Promise<void> => {
        if (!isLoggedIn.value) {
            userGatedAction.value = { action: UserGatedAction.ADP_BID };
            goToLoginPage();

            return;
        }

        if (shouldShowInspectionExpiredModal.value) {
            trackAdpAlertView('expired_revision');
            if (!(await inspectionExpiredModalState.open())) return;
        }

        if (isBailNeeded.value) {
            if (!(await bailModalState.open())) return;

            await until(isBailNeeded).toBe(false);
        }

        if (!userMeta.value?.canBid) {
            return;
        }

        if (await bidModalState.open()) {
            await queryClient.refetchQueries({ queryKey: [AUCTION_DETAIL_QUERY_KEY_PREFIX], type: 'active' }); // Refetch not strictly necessary, redundant with update via WS
            updateTrackingCompanyMetrics();
        }
    })();

    bidFlowInProgress.value = false;
};

const closeModals = (): void => {
    inspectionExpiredModalState.cancel();
    bailModalState.cancel();
    bidModalState.cancel();
};

// LIFECYCLE
onMounted(() => {
    if (isLoggedIn.value && userGatedAction.value?.action === UserGatedAction.ADP_BID) {
        userGatedAction.value = null;
        bidFlow();
    }
});

// WATCHERS
watch(isLoggedIn, () => {
    // Reset internal state on login change
    closeModals();
});

watch(
    () => auction.state,
    value => {
        if ([AuctionState.Expired, AuctionState.Canceled].includes(value) && bidFlowInProgress.value) {
            closeModals();
            notifyError({
                title: tt(`sdp.Auction.bid.expiredError.title`),
                description: tt(`sdp.Auction.bid.expiredError.description`),
                duration: NotificationDuration.NoTimeout,
            });
        }
    }
);
</script>

<template>
    <div v-if="showSection" class="flex flex-col justify-center bg-white p-4 lg:p-6">
        <div class="flex flex-col gap-3">
            <SdpSalePanelNoticeRestricted v-if="hasRestrictedNotice" :sale="auction" />
            <template v-else>
                <SdpSalePanelNoticeBase
                    v-if="hasInspectionNotice"
                    :message="tt(`sdp.notice.inspectionExpired`)"
                    icon-name="lucide:triangle-alert"
                    icon-class="text-warning-500"
                />

                <template v-if="hasPlaceBid">
                    <span v-if="activeAutoBidAmount">
                        {{ tt(`sdp.Auction.bid.activeAutoBid`) }}:
                        <span class="text-primary font-medium">
                            {{ tn(activeAutoBidAmount, 'currency') }}
                        </span>
                    </span>

                    <BaseIconBgButton
                        size="lg"
                        color="secondary"
                        :ui="buttonResponsiveSizeUi('lg', 'xl')"
                        variant="solid"
                        :label="bidButtonLabel"
                        block
                        :loading="bidFlowInProgress"
                        @click="bidFlow"
                    />

                    <LazySdpInspectionExpiredModal
                        v-if="shouldShowInspectionExpiredModal"
                        v-model="inspectionExpiredModalState.isOpen"
                        @confirm="inspectionExpiredModalState.confirm(true)"
                    />

                    <LazyAdpBailModal
                        v-if="isBailNeeded"
                        v-model="bailModalState.isOpen"
                        :auction="auction"
                        @success="bailModalState.confirm(true)"
                    />

                    <LazyAdpBidModal
                        v-if="userMeta?.canBid && isAuctionItemEntityWithCurrentPrince(auction)"
                        v-model="bidModalState.isOpen"
                        :auction="auction"
                        @success="bidModalState.confirm(true)"
                    />
                </template>
            </template>

            <SdpSalePanelGoToNegotiation v-if="hasGoToNegotiation" :sale="auction" />

            <SdpSalePanelVas v-if="!isSeller" :sale="auction" />
        </div>
    </div>
</template>
